import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaEye, FaPencilAlt, FaTrash, FaSpinner, FaPlus, FaSearch, FaMinusCircle } from 'react-icons/fa';
import ContactModal from '../../components/ContactModal';
import { apiCall, ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { Contact, ContactType, ContactFormData } from '../../types/contact';

interface SortConfig {
        key: keyof Contact | null;
        direction: 'ascending' | 'descending';
}

type CommunicationField = 'argus' | 'health_link' | 'medical_objects' | 'email' | 'fax';

const AddressBookReferrers = () => {
        const { token } = useAuth();
        const [searchTerm, setSearchTerm] = useState('');
        const [selectedReferrers, setSelectedReferrers] = useState<string[]>([]);
        const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: 'ascending' });
        const [activeTab, setActiveTab] = useState('All');
        const [isModalOpen, setIsModalOpen] = useState(false);
        const [editingReferrer, setEditingReferrer] = useState<Contact | null>(null);
        const [referrers, setReferrers] = useState<Contact[]>([]);
        const [contactTypes, setContactTypes] = useState<ContactType[]>([]);
        const [isLoading, setIsLoading] = useState(true);
        const [error, setError] = useState<string | null>(null);

        const tabs = [
                { id: 'All', label: 'All' },
                { id: 'Active', label: 'Active' },
                { id: 'Inactive', label: 'Inactive' },
        ];

        useEffect(() => {
                loadReferrers();
                loadContactTypes();
        }, []);

        useEffect(() => {
                loadReferrers();
        }, [activeTab, searchTerm]);

        const loadReferrers = async () => {
                setIsLoading(true);
                try {
                        let url = ENDPOINTS.CORE.CONTACTS;
                        const params = new URLSearchParams();
                        params.append('is_referrer', 'true');

                        if (activeTab !== 'All') {
                                params.append('is_active', activeTab === 'Active' ? 'true' : 'false');
                        }

                        if (searchTerm) {
                                params.append('search', searchTerm);
                        }

                        url += `?${params.toString()}`;

                        const response = await apiCall(url, { method: 'GET' }, token || undefined);
                        if (response.ok) {
                                const data = await response.json();
                                setReferrers(data.results || data);
                                setError(null);
                        } else {
                                throw new Error(`Failed to load referrers: ${response.status} ${response.statusText}`);
                        }
                } catch (err) {
                        console.error('Error loading referrers:', err);
                        setError('Failed to load referrers');
                } finally {
                        setIsLoading(false);
                }
        };

        const loadContactTypes = async () => {
                if (!token) return;
                try {
                        const response = await apiCall(`${ENDPOINTS.CORE.CONTACT_TYPES}?is_active=true&page_size=200`, { method: 'GET' }, token);
                        if (response.ok) {
                                const data = await response.json();
                                setContactTypes(data.results || data);
                        }
                } catch (err) {
                        console.error('Error loading contact types:', err);
                }
        };

        const handleAddReferrer = () => {
                setEditingReferrer(null);
                setIsModalOpen(true);
        };

        const handleEditReferrer = async (referrer: Contact) => {
                if (!referrer.id) return;
                try {
                        const response = await apiCall(`${ENDPOINTS.CORE.CONTACTS}${referrer.id}/`, { method: 'GET' }, token || undefined);
                        if (response.ok) {
                                const fullContact = await response.json();
                                setEditingReferrer(fullContact);
                                setIsModalOpen(true);
                        } else {
                                console.error('Failed to load referrer details:', response.status, response.statusText);
                        }
                } catch (err) {
                        console.error('Error loading referrer details:', err);
                }
        };

        const handleSaveReferrer = async (referrerData: ContactFormData) => {
                try {
                        const referrerType = contactTypes.find(type => type.name.toLowerCase() === 'referrer');
                        const payload = referrerType ? { ...referrerData, contact_type: referrerType.id } : referrerData;

                        const url = editingReferrer
                                ? `${ENDPOINTS.CORE.CONTACTS}${editingReferrer.id}/`
                                : ENDPOINTS.CORE.CONTACTS;
                        const method = editingReferrer ? 'PUT' : 'POST';

                        const response = await apiCall(url, {
                                method,
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify(payload),
                        }, token || undefined);

                        if (response.ok) {
                                await loadReferrers();
                                setIsModalOpen(false);
                                setEditingReferrer(null);
                        } else {
                                const errorData = await response.json();
                                throw new Error(errorData.message || 'Failed to save referrer');
                        }
                } catch (err) {
                        console.error('Error saving referrer:', err);
                        throw err;
                }
        };

        const handleDeleteReferrer = async (referrerId: string) => {
                if (!confirm('Are you sure you want to delete this referrer?')) {
                        return;
                }

                try {
                        const response = await apiCall(`${ENDPOINTS.CORE.CONTACTS}${referrerId}/`, { method: 'DELETE' }, token || undefined);
                        if (response.ok) {
                                await loadReferrers();
                        } else {
                                throw new Error('Failed to delete referrer');
                        }
                } catch (err) {
                        console.error('Error deleting referrer:', err);
                        alert('Failed to delete referrer');
                }
        };

        const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
                if (e.target.checked) {
                        setSelectedReferrers(referrers.map(referrer => referrer.id).filter((id): id is string => !!id));
                } else {
                        setSelectedReferrers([]);
                }
        };

        const handleSelectOne = (referrerId: string) => {
                setSelectedReferrers(prev =>
                        prev.includes(referrerId) ? prev.filter(id => id !== referrerId) : [...prev, referrerId]
                );
        };

        const requestSort = (key: keyof Contact) => {
                let direction: SortConfig['direction'] = 'ascending';
                if (sortConfig.key === key && sortConfig.direction === 'ascending') {
                        direction = 'descending';
                }
                setSortConfig({ key, direction });
        };

        const formatPhoneNumber = (phone?: string) => {
                if (!phone) return '';
                const cleaned = phone.replace(/\D/g, '');

                if (cleaned.startsWith('04')) {
                        return cleaned.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3');
                } else if (/^0[2378]/.test(cleaned)) {
                        return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '$1 $2 $3');
                }
                return phone;
        };

        const getCommunicationBadge = (type: CommunicationField, value?: string) => {
                if (!value) return null;

                const badgeColors: Record<CommunicationField, string> = {
                        argus: 'bg-blue-100 text-blue-800',
                        health_link: 'bg-green-100 text-green-800',
                        medical_objects: 'bg-purple-100 text-purple-800',
                        email: 'bg-amber-100 text-amber-800',
                        fax: 'bg-gray-100 text-gray-800'
                };

                const labels: Record<CommunicationField, string> = {
                        argus: 'Argus',
                        health_link: 'HealthLink',
                        medical_objects: 'MedicalObjects',
                        email: 'Email',
                        fax: 'Fax'
                };

                return (
                        <span key={type} className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${badgeColors[type]} mr-2 mb-1`}>
                                {labels[type]}
                        </span>
                );
        };

        const getSortedReferrers = () => {
                const sorted = [...referrers];

                if (sortConfig.key) {
                        const key = sortConfig.key;
                        sorted.sort((a, b) => {
                                const aVal = a[key];
                                const bVal = b[key];
                                if (aVal == null || bVal == null) return 0;
                                if (aVal < bVal) return sortConfig.direction === 'ascending' ? -1 : 1;
                                if (aVal > bVal) return sortConfig.direction === 'ascending' ? 1 : -1;
                                return 0;
                        });
                }

                return sorted;
        };

        const sortIndicator = (key: keyof Contact) => {
                if (sortConfig.key !== key) return '';
                return sortConfig.direction === 'ascending' ? ' ▲' : ' ▼';
        };

        return (
                <div className="p-6">
                        <div className="flex items-center gap-3 mb-6">
                                <h1 className="text-2xl font-semibold text-base-content shrink-0">Address Book • Referrers</h1>
                                <div className="flex items-center gap-2 input input-bordered input-sm w-64 shrink-0">
                                        <FaSearch className="text-base-content/40 flex-shrink-0 text-xs" />
                                        <input
                                                type="text"
                                                placeholder="Search referrers..."
                                                className="flex-1 bg-transparent outline-none text-sm"
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                </div>
                                <button className="btn btn-primary btn-sm shrink-0 ml-auto" onClick={handleAddReferrer} disabled={isLoading}>
                                        <FaPlus className="mr-2" />
                                        Add Referrer
                                </button>
                        </div>

                        <div className="tabs tabs-bordered mb-6">
                                {tabs.map((tab) => (
                                        <button
                                                key={tab.id}
                                                className={`tab tab-lg ${activeTab === tab.id ? 'tab-active' : ''}`}
                                                onClick={() => setActiveTab(tab.id)}
                                        >
                                                {tab.label}
                                                {tab.id !== 'All' && (
                                                        <span className="ml-2 badge badge-sm">
                                                                {referrers.filter(r => (tab.id === 'Active') === !!r.is_active).length}
                                                        </span>
                                                )}
                                        </button>
                                ))}
                        </div>

                        {error && (
                                <div className="alert alert-error mb-6">
                                        <span>{error}</span>
                                </div>
                        )}

                        {isLoading ? (
                                <div className="flex justify-center items-center h-64">
                                        <FaSpinner className="animate-spin text-4xl" />
                                        <span className="ml-4 text-lg">Loading referrers...</span>
                                </div>
                        ) : (
                                <div className="relative overflow-x-auto rounded-lg">
                                        <table className="table table-zebra bg-base-100/50">
                                                <thead>
                                                        <tr>
                                                                <th className="w-4">
                                                                        <input
                                                                                type="checkbox"
                                                                                className="checkbox checkbox-sm"
                                                                                onChange={handleSelectAll}
                                                                                checked={referrers.length > 0 && selectedReferrers.length === referrers.length}
                                                                        />
                                                                </th>
                                                                <th className="cursor-pointer" onClick={() => requestSort('salutation')}>Salutation{sortIndicator('salutation')}</th>
                                                                <th className="cursor-pointer" onClick={() => requestSort('first_name')}>First Name{sortIndicator('first_name')}</th>
                                                                <th className="cursor-pointer" onClick={() => requestSort('last_name')}>Last Name{sortIndicator('last_name')}</th>
                                                                <th className="cursor-pointer" onClick={() => requestSort('specialty')}>Specialty{sortIndicator('specialty')}</th>
                                                                <th className="cursor-pointer" onClick={() => requestSort('company')}>Clinic{sortIndicator('company')}</th>
                                                                <th>Contact</th>
                                                                <th>Available Communications</th>
                                                                <th>Commands</th>
                                                        </tr>
                                                </thead>
                                                <tbody>
                                                        {getSortedReferrers().length === 0 ? (
                                                                <tr>
                                                                        <td colSpan={9} className="text-center py-8">No referrers found</td>
                                                                </tr>
                                                        ) : (
                                                                getSortedReferrers().map((referrer) => (
                                                                        <tr key={referrer.id}>
                                                                                <td>
                                                                                        {referrer.id && (
                                                                                                <input
                                                                                                        type="checkbox"
                                                                                                        className="checkbox checkbox-sm"
                                                                                                        checked={selectedReferrers.includes(referrer.id)}
                                                                                                        onChange={() => referrer.id && handleSelectOne(referrer.id)}
                                                                                                />
                                                                                        )}
                                                                                </td>
                                                                                <td>{referrer.salutation}</td>
                                                                                <td>
                                                                                        <Link
                                                                                                to={`/address-book/referrer/${referrer.id}`}
                                                                                                className="hover:underline cursor-pointer"
                                                                                        >
                                                                                                {referrer.first_name}
                                                                                        </Link>
                                                                                </td>
                                                                                <td>{referrer.last_name}</td>
                                                                                <td>{referrer.specialty}</td>
                                                                                <td>{referrer.company}</td>
                                                                                <td>
                                                                                        <div>{formatPhoneNumber(referrer.phone)}</div>
                                                                                        <div className="text-sm opacity-60">{referrer.email}</div>
                                                                                </td>
                                                                                <td>
                                                                                        <div className="flex flex-wrap">
                                                                                                {getCommunicationBadge('argus', referrer.argus)}
                                                                                                {getCommunicationBadge('health_link', referrer.health_link)}
                                                                                                {getCommunicationBadge('medical_objects', referrer.medical_objects)}
                                                                                                {getCommunicationBadge('email', referrer.email)}
                                                                                                {getCommunicationBadge('fax', referrer.fax)}
                                                                                        </div>
                                                                                </td>
                                                                                <td>
                                                                                        <div className="flex gap-2">
                                                                                                <Link to={`/address-book/referrer/${referrer.id}`} className="btn btn-ghost btn-xs">
                                                                                                        <FaEye className="w-4 h-4" />
                                                                                                </Link>
                                                                                                <button className="btn btn-ghost btn-xs" onClick={() => handleEditReferrer(referrer)}>
                                                                                                        <FaPencilAlt className="w-4 h-4" />
                                                                                                </button>
                                                                                                {referrer.is_active ? (
                                                                                                        <button
                                                                                                                className="btn btn-ghost btn-xs"
                                                                                                                title="Mark inactive"
                                                                                                                onClick={() => referrer.id && handleDeleteReferrer(referrer.id)}
                                                                                                        >
                                                                                                                <FaMinusCircle className="w-4 h-4" />
                                                                                                        </button>
                                                                                                ) : (
                                                                                                        <button
                                                                                                                className="btn btn-ghost btn-xs text-error"
                                                                                                                title="Delete"
                                                                                                                onClick={() => referrer.id && handleDeleteReferrer(referrer.id)}
                                                                                                        >
                                                                                                                <FaTrash className="w-4 h-4" />
                                                                                                        </button>
                                                                                                )}
                                                                                        </div>
                                                                                </td>
                                                                        </tr>
                                                                ))
                                                        )}
                                                </tbody>
                                        </table>
                                </div>
                        )}

                        <ContactModal
                                isOpen={isModalOpen}
                                onClose={() => { setIsModalOpen(false); setEditingReferrer(null); }}
                                onSave={handleSaveReferrer}
                                editContact={editingReferrer}
                                contactTypes={contactTypes}
                        />
                </div>
        );
};

export default AddressBookReferrers;
